import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { getSceneVersion } from '@excalidraw/excalidraw';
import { type ImportedDataState } from '@excalidraw/excalidraw/types/data/types';
import {
    type ExcalidrawElement,
    type ExcalidrawImageElement,
    type NonDeletedExcalidrawElement
} from '@excalidraw/excalidraw/types/element/types';
import { type AppState, type BinaryFileData, type BinaryFiles } from '@excalidraw/excalidraw/types/types';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Stack, Typography, styled } from '@mui/material';
import _ from 'lodash';
import { useHotkeys } from 'react-hotkeys-hook';
import { useTranslation } from 'react-i18next';

import { ExcalidrawWrapper } from './ExcalidrawWrapper';
import { ExportModal } from './interface/export/ExportModal';
import MoveCanvasHelpChip from './interface/MoveCanvasHelpChip';
import ElementOptionsPanel from './interface/options/ElementOptionsPanel';
import ZoomTools, { getNormalizedZoom, getStateForZoom } from './interface/ZoomTools';
import Loading from '../../../pages/Loading';
import { getUrlParam, getUrls } from '../../../utils/url';
import { usePrevious } from '../../../utils/usePrevious';
import { WhiteboardContext } from '../context/WhiteboardContext';
import { WhiteboardSessionContext } from '../context/WhiteboardSessionContext';
import { ElementActions, FileActions } from '../state/WhiteboardSessionState';
import { type InitialPageData, SessionTypes } from '../types';
import {
    moveBackward,
    moveForward,
    type MoveOperation,
    moveToBack,
    moveToFront,
    sortElements
} from '../util/layer';
import { getLibraries, getNewFilesFromElements, log } from '../util/misc';

const Note = styled(Box)`
    padding: 8px 24px 16px;
    color: #fff;
    background: #212121;
    width: 100%;
    min-height: 48px;
    max-height: 180px;
    overflow-y: auto;
    position: relative;
`;

const Layout = styled(Box)`
    width: 100%;
    height: calc(100% - 48px);
    display: flex;
    flex-flow: column;
`;

const InnerLayout = styled(Box)`
    width: 100%;
    position: relative;
    flex: 1;
    display: flex;
`;

const SecondaryControls = styled(Box)`
    background: #fff;
    width: 48px;
    padding: 4px;
    position: absolute;
    left: 12px;
    top: 14px;
    z-index: 2;
    border-radius: 4px;
`;

const WhiteboardContent = styled(Box)`
    flex-grow: 1;
    padding: 0;
`;

const CloseNotesIcon = styled(CloseIcon)`
    color: #fff;
    position: absolute;
    top: 4px;
    right: 4px;

    &:hover {
        cursor: pointer;
    }
`;

// How frequent local element changes are saved to backend. Also, when remote changes are applied to canvas in batches.
const SYNC_INTERVAL = 250;
const REMOTE_SYNC_INTERVAL = 500;
const SYNC_VIEWPORT_INTERVAL = 250;

const MOVE_BACKWARD_HOTKEYS = 'ctrl+[, command+[';
const MOVE_FORWARD_HOTKEYS = 'ctrl+], command+]';
const MOVE_TO_BACK_HOTKEYS = 'ctrl+alt+[, command+option+[';
const MOVE_TO_FRONT_HOTKEYS = 'ctrl+alt+], command+option+]';
const ALL_HOTKEYS = [MOVE_BACKWARD_HOTKEYS, MOVE_FORWARD_HOTKEYS, MOVE_TO_BACK_HOTKEYS, MOVE_TO_FRONT_HOTKEYS].join(
    ', '
);

type ExcalidrawControllerProps = {
    readonly: boolean;
    isInControl?: boolean;
};

// eslint-disable-next-line @typescript-eslint/no-var-requires
const libraryManifest = require('../libraries/all.json');

/**
 * Embedded files ship with the app (as opposed to user uploaded).
 */
type EmbeddedFileData = BinaryFileData & {
    embedded: boolean;
};
type EmbeddedFiles = Record<ExcalidrawElement['id'], BinaryFileData | EmbeddedFileData>;

/**
 * Check if an image element contains an embedded file (base64 encoded instead of an image URL).
 * @param element
 * @param localFiles
 */
function isImageElementWithEmbeddedFile(
    element: ExcalidrawElement,
    localFiles: BinaryFiles | EmbeddedFiles
): element is ExcalidrawImageElement {
    if (element.type !== 'image' || !element.fileId || !localFiles[element.fileId]) {
        return false;
    }
    const file = localFiles[element.fileId];
    return 'embedded' in file && file.embedded;
}

/**
 * A single whiteboard page.
 */
export const ExcalidrawController: React.FC<ExcalidrawControllerProps> = ({ readonly, isInControl }) => {
    const { getInitialData, delegateImage, userInfo, saveElements, deleteElements } = useContext(WhiteboardContext);
    const {
        whiteboardSessionState,
        dispatchWhiteboardSessionAction,
        persist,
        mergeRemoteChanges,
        excalidrawAPIRef,
        excalidrawAPI,
        excalidrawRefCallback,
        activeTool,
        setActiveTool,
        showNotes,
        setShowNotes,
        setElementOptionsPanel,
        setZoomTools,
        clearPage
    } = useContext(WhiteboardSessionContext);
    const { presenter, currentPage, templatePage, createdElements, deletedElementIds, session, pages } =
        whiteboardSessionState;

    const { t } = useTranslation('whiteboard');

    const hideToolbar = getUrlParam('hideToolbar') === 'true';

    const previousPage = usePrevious(currentPage);
    const excludedImageElementIdsRef = useRef<string[]>([]);
    const fileIds = useRef<string[]>([]);
    const elementVersions = useRef<Map<string, number>>(new Map());
    const elementVersionNonces = useRef<Map<string, number>>(new Map());
    const lastTrackedVersion = useRef<number>(0);
    const lastIntervalTime = useRef<number>(0);
    const elementsToUpdate = useRef<Map<string, any>>(new Map());

    const editingElements = useRef<Map<string, any>>(new Map());

    const [initialData, setInitialData] = useState<{ key: string; data: ImportedDataState } | undefined>();
    const [loading, setLoading] = useState<boolean>(true);
    const [selectedElementIds, setSelectedElementIds] = useState<string[]>([]);
    const [lastSelectionElement, setLastSelectionElement] = useState<ExcalidrawElement | null>(null);
    const lastSelectedElementIds = useRef<string[]>([]);
    const [zoom, setZoom] = useState<number>(1);
    const [gridModeEnabled, enableGridMode] = useState(false);
    const [exportModalOpen, setExportModalOpen] = useState<boolean>(false);

    const [hasTriedToResize, setHasTriedToResize] = useState<boolean>(false);

    const [viewPort, setViewPort] = useState<any>(
        (window as any).viewPort || {
            x: 0,
            y: 0,
            width: 1,
            height: 1,
            zoom: session?.type === (SessionTypes.Presentation || SessionTypes.Annotations) ? 0.1 : 1,
            forceFit: session?.type === SessionTypes.Annotations ? true : false,
            activeTool: 'hand'
        }
    );

    /**
     * Follow presenter.
     */
    useEffect(() => {
        setHasTriedToResize(false);
    }, [currentPage, setHasTriedToResize]);

    /**
     * Follow presenter.
     */
    useEffect(() => {
        if (loading) {
            return;
        }
        if (!excalidrawAPIRef.current) {
            return;
        }
        if (hasTriedToResize) {
            return;
        }
        const elements = excalidrawAPIRef.current?.getSceneElements();
        if (!elements || elements.length === 0) {
            return;
        }
        const hasBounds =
            session?.type === SessionTypes.Presentation ||
            excalidrawAPIRef.current?.getSceneElements().some((element) => element.customData?.isBackground);
        setHasTriedToResize(true);
        if (!hasBounds) {
            return;
        }

        let bounds;
        let backgroundCount = 0;
        elements?.forEach((element) => {
            if (element?.customData?.isBackground) {
                if (bounds) {
                    let newMinX = Math.min(bounds.x, element.x);
                    let newMinY = Math.min(bounds.y, element.y);
                    let newMaxX = Math.max(bounds.x + bounds.width, element.x + element.width);
                    let newMaxY = Math.max(bounds.y + bounds.height, element.y + element.height);

                    bounds = {
                        x: newMinX,
                        y: newMinY,
                        width: newMaxX - newMinX,
                        height: newMaxY - newMinY
                    };
                } else {
                    bounds = {
                        x: element.x,
                        y: element.y,
                        width: element.width,
                        height: element.height
                    };
                }
                backgroundCount = backgroundCount + 1;
            }
        });
        let zoom = 0.1;
        if (backgroundCount > 1) {
            zoom = 1;
        }
        setViewPort({
            x: 0,
            y: 0,
            width: 1,
            height: 1,
            zoom
        });
        excalidrawAPIRef.current.updateScene({
            appState: {
                zoom: { value: zoom },
                viewBackgroundColor: '#eee'
            }
        });
    }, [currentPage, excalidrawAPIRef, hasTriedToResize, setHasTriedToResize, setViewPort, session?.type, loading]);

    excalidrawAPIRef.current = excalidrawAPI ?? undefined;

    /**
     * Keep track of element versions to determine which were changed locally. Excalidraw does not have a callback for
     * changed elements. Every change is supplied with all elements.
     */
    const updateVersionTracker = useCallback((elements: ExcalidrawElement[] | readonly ExcalidrawElement[]) => {
        elementVersionNonces.current = new Map();
        elementVersions.current = new Map();
        elements.forEach((element) => {
            elementVersionNonces.current.set(element.id, element.versionNonce);
            elementVersions.current.set(element.id, element.version);
        });
        lastTrackedVersion.current = getSceneVersion(elements);
    }, []);

    const libraryGroups = useMemo(() => {
        const { vanityUrl } = getUrls();
        return getLibraries(vanityUrl, libraryManifest);
    }, []);

    /**
     * Follow presenter.
     */
    useEffect(() => {
        if (!excalidrawAPIRef.current || !userInfo) return;
        if (session?.type === SessionTypes.Presentation) return;

        const backgroundElements = excalidrawAPIRef.current
            ?.getSceneElements()
            .filter((element) => element.customData?.isBackground);
        const hasBounds = backgroundElements.length === 1;
        if (hasBounds) {
            return;
        }
        // Control local canvas if someone else is presenting.
        if (presenter && presenter.student_id !== userInfo.student_id) {
            const appState = excalidrawAPIRef.current.getAppState();
            let zoomValue = presenter.zoom ?? appState.zoom;
            try {
                const minRatio = Math.max(presenter!.width! / appState.width, presenter!.height! / appState.height);
                zoomValue = {
                    value: getNormalizedZoom(Number(presenter!.zoom!.value) / minRatio)
                };
            } catch (err) {}
            console.log('zoomValue', zoomValue);

            let newState = {
                bounds: {
                    x: -(presenter.scrollX ?? appState.scrollX),
                    y: -(presenter.scrollY ?? appState.scrollY),
                    width: (presenter.width || 1000) / (presenter.zoom?.value || 1),
                    height: (presenter.height || 1000) / (presenter.zoom?.value || 1)
                }
            } as any;
            //if (session?.type === SessionTypes.Annotations && !isInControl) {
            //    newState.zoom = {
            //        value: 0.1
            //    };
            //}
            excalidrawAPIRef.current.updateScene({
                appState: newState
            });
        } else if (presenter === null) {
            excalidrawAPIRef.current.updateScene({
                appState: {
                    bounds: null
                }
            });
        }
    }, [presenter, userInfo, viewPort, excalidrawAPIRef, session?.type, isInControl]);

    useEffect(() => {
        const onMessageHandler = (event: MessageEvent) => {
            //console.log('onMessageHandler', event);
            try {
                let { data: eventData } = event;
                let { data, func } = eventData;
                if (func === 'whiteboard_update_viewport') {
                    excalidrawAPIRef.current.updateScene({
                        appState: data
                    });
                } else if (func === 'whiteboard_clear_page') {
                    clearPage();
                }
            } catch (err) {}
        };
        window.addEventListener('message', onMessageHandler);
        return () => {
            window.removeEventListener('message', onMessageHandler);
        };
    }, [excalidrawAPIRef, clearPage]);

    /**
     * Get initial data for group whiteboards
     */
    useEffect(() => {
        if (!session || !currentPage) return;
        if (
            session.type !== SessionTypes.Annotations &&
            session.type !== SessionTypes.Shared &&
            session.type !== SessionTypes.Presentation
        )
            return;
        if (currentPage.page_id === previousPage?.page_id) return;

        const setupSharedWhiteboardPage = async () => {
            setLoading(true);

            log('Loading new page');

            // Persist any leftover elements from previous page.
            //if (currentPage && excalidrawAPIRef.current) {
            //    const allIds = persist(excalidrawAPIRef.current.getAppState());
            //    dispatchWhiteboardSessionAction({ type: ElementActions.Clear, payload: { elementIds: allIds } });
            //}

            // Preload library files, so they are not mistaken for new files.
            fileIds.current = libraryGroups.reduce(
                (prev, library) => ((library as any).files ? [...prev, ...Object.keys((library as any).files)] : prev),
                fileIds.current
            );

            const data = await getInitialData(currentPage, libraryGroups);
            updateVersionTracker(data.elements || []);
            data.session = session;

            log('Loaded initial data', data);

            if (_.isUndefined(data.appState)) {
                data.appState = {};
            }
            data.appState.bounds = initialData?.data?.appState?.bounds || null;
            let bounds;
            let backgroundCount = 0;
            data?.elements?.forEach((element) => {
                if (element?.customData?.isBackground) {
                    if (bounds) {
                        let newMinX = Math.min(bounds.x, element.x);
                        let newMinY = Math.min(bounds.y, element.y);
                        let newMaxX = Math.max(bounds.x + bounds.width, element.x + element.width);
                        let newMaxY = Math.max(bounds.y + bounds.height, element.y + element.height);

                        bounds = {
                            x: newMinX,
                            y: newMinY,
                            width: newMaxX - newMinX,
                            height: newMaxY - newMinY
                        };
                    } else {
                        bounds = {
                            x: element.x,
                            y: element.y,
                            width: element.width,
                            height: element.height
                        };
                    }
                    backgroundCount = backgroundCount + 1;
                }
            });
            if (bounds) {
                data.appState!.bounds = bounds;
            }
            if (backgroundCount > 1) {
                data.appState!.zoom = {
                    value: 1 as any
                }
            }
            if (backgroundCount > 0) {
                data.appState.viewBackgroundColor = '#f0f0f0';
            }

            setInitialData({ key: currentPage.page_id, data });
            setLoading(false);
        };

        setupSharedWhiteboardPage();
    }, [
        session,
        currentPage,
        initialData,
        libraryGroups,
        getInitialData,
        excalidrawAPIRef,
        updateVersionTracker,
        previousPage?.page_id,
        dispatchWhiteboardSessionAction
    ]);

    /**
     * Get initial data for individual whiteboards
     */
    useEffect(() => {
        if (!session || !currentPage) return;
        if (session.type !== SessionTypes.Individual) return;
        if (currentPage.page_id === previousPage?.page_id) return;

        const setupIndividualWhiteboardPage = async () => {
            setLoading(true);

            log('Loading new page');

            // Persist any leftover elements from previous page.
            //if (currentPage && excalidrawAPIRef.current) {
            //    const allIds = persist(excalidrawAPIRef.current.getAppState());
            //    dispatchWhiteboardSessionAction({ type: ElementActions.Clear, payload: { elementIds: allIds } });
            //}

            // Preload library files, so they are not mistaken for new files.
            fileIds.current = libraryGroups.reduce(
                (prev, library) => ((library as any).files ? [...prev, ...Object.keys((library as any).files)] : prev),
                fileIds.current
            );

            const data: InitialPageData = {
                elements: [],
                files: {},
                session
            };

            /**
             * Combine template elements with target page elements if
             * a template page is present.
             */
            if (templatePage && currentPage.page_id !== templatePage?.page_id) {
                const [pageData, templateData] = await Promise.all([
                    getInitialData(currentPage, libraryGroups),
                    getInitialData(templatePage, libraryGroups)
                ]);

                data.elements = [...pageData.elements, ...templateData.elements];
                data.files = _.merge(pageData.files, templateData.files);

                /**
                 * Lock template elements if present on a
                 * page other than the template page.
                 */
                if (currentPage.page_id !== templatePage.page_id) {
                    data.elements = templateData.elements.map((element) => ({ ...element, locked: true }));
                    data.elements = data.elements.concat(pageData.elements);
                }
            } else {
                const pageData = await getInitialData(currentPage, libraryGroups);

                data.elements = pageData.elements;
                data.files = pageData.files;
            }
            data.session = session;

            updateVersionTracker(data.elements || []);

            log('Loaded initial data', data);
            if (_.isUndefined(data.appState)) {
                data.appState = {};
            }
            data.appState.bounds = initialData?.data?.appState?.bounds || null;
            let bounds;
            let backgroundCount = 0;
            data?.elements?.forEach((element) => {
                if (element?.customData?.isBackground) {
                    if (bounds) {
                        let newMinX = Math.min(bounds.x, element.x);
                        let newMinY = Math.min(bounds.y, element.y);
                        let newMaxX = Math.max(bounds.x + bounds.width, element.x + element.width);
                        let newMaxY = Math.max(bounds.y + bounds.height, element.y + element.height);

                        bounds = {
                            x: newMinX,
                            y: newMinY,
                            width: newMaxX - newMinX,
                            height: newMaxY - newMinY
                        };
                    } else {
                        bounds = {
                            x: element.x,
                            y: element.y,
                            width: element.width,
                            height: element.height
                        };
                    }
                    backgroundCount = backgroundCount + 1;
                }
            });
            if (bounds) {
                data.appState!.bounds = bounds;
            }
            if (backgroundCount > 1) {
                data.appState!.zoom = {
                    value: 1 as any
                }
            }
            if (backgroundCount > 0) {
                data.appState.viewBackgroundColor = '#f0f0f0';
            }

            setInitialData({ key: currentPage.page_id, data });
            setLoading(false);
        };

        setupIndividualWhiteboardPage();
    }, [
        pages,
        session,
        initialData,
        currentPage,
        templatePage,
        libraryGroups,
        getInitialData,
        excalidrawAPIRef,
        updateVersionTracker,
        previousPage?.page_id,
        dispatchWhiteboardSessionAction
    ]);

    /**
     * Get initial data for breakout whiteboards
     */
    useEffect(() => {
        if (!session || !currentPage) return;
        if (session.type !== SessionTypes.Breakout) return;
        if (currentPage.page_id === previousPage?.page_id) return;

        const setupBreakoutWhiteboardPage = async () => {
            setLoading(true);

            log('Loading new page');

            // Persist any leftover elements from previous page.
            //if (currentPage && excalidrawAPIRef.current) {
            //    const allIds = persist(excalidrawAPIRef.current.getAppState());
            //    dispatchWhiteboardSessionAction({ type: ElementActions.Clear, payload: { elementIds: allIds } });
            //}

            // Preload library files, so they are not mistaken for new files.
            fileIds.current = libraryGroups.reduce(
                (prev, library) => ((library as any).files ? [...prev, ...Object.keys((library as any).files)] : prev),
                fileIds.current
            );

            const data: InitialPageData = {
                elements: [],
                files: {},
                session
            };

            /**
             * Combine template elements with target page elements if
             * a template page is present.
             */
            if (templatePage && currentPage.page_id !== templatePage?.page_id) {
                const [pageData, templateData] = await Promise.all([
                    getInitialData(currentPage, libraryGroups),
                    getInitialData(templatePage, libraryGroups)
                ]);

                data.elements = [...pageData.elements, ...templateData.elements];
                data.files = _.merge(pageData.files, templateData.files);

                /**
                 * Lock template elements if present on a
                 * page other than the template page.
                 */
                if (currentPage.page_id !== templatePage.page_id) {
                    data.elements = templateData.elements.map((element) => ({ ...element, locked: true }));
                    data.elements = data.elements.concat(pageData.elements);
                }
            } else {
                const pageData = await getInitialData(currentPage, libraryGroups);

                data.elements = pageData.elements;
                data.files = pageData.files;
            }
            data.session = session;

            updateVersionTracker(data.elements || []);

            log('Loaded initial data', data);
            if (_.isUndefined(data.appState)) {
                data.appState = {};
            }
            data.appState.bounds = initialData?.data?.appState?.bounds || null;
            let bounds;
            let backgroundCount = 0;
            data?.elements?.forEach((element) => {
                if (element?.customData?.isBackground) {
                    if (bounds) {
                        let newMinX = Math.min(bounds.x, element.x);
                        let newMinY = Math.min(bounds.y, element.y);
                        let newMaxX = Math.max(bounds.x + bounds.width, element.x + element.width);
                        let newMaxY = Math.max(bounds.y + bounds.height, element.y + element.height);

                        bounds = {
                            x: newMinX,
                            y: newMinY,
                            width: newMaxX - newMinX,
                            height: newMaxY - newMinY
                        };
                    } else {
                        bounds = {
                            x: element.x,
                            y: element.y,
                            width: element.width,
                            height: element.height
                        };
                    }
                    backgroundCount = backgroundCount + 1;
                }
            });
            if (bounds) {
                data.appState!.bounds = bounds;
            }
            if (backgroundCount > 1) {
                data.appState!.zoom = {
                    value: 1 as any
                }
            }
            if (backgroundCount > 0) {
                data.appState.viewBackgroundColor = '#f0f0f0';
            }

            setInitialData({ key: currentPage.page_id, data });
            setLoading(false);
        };

        setupBreakoutWhiteboardPage();
    }, [
        pages,
        session,
        initialData,
        currentPage,
        templatePage,
        libraryGroups,
        getInitialData,
        excalidrawAPIRef,
        updateVersionTracker,
        previousPage?.page_id,
        dispatchWhiteboardSessionAction
    ]);

    /**
     * Applies queued remote changes to Excalidraw and local state.
     */
    const applyRemoteUpdates = useCallback(() => {
        if (!excalidrawAPIRef.current) {
            return;
        }

        let mergedElements = sortElements(
            mergeRemoteChanges(
                excalidrawAPIRef.current.getSceneElementsIncludingDeleted() as ExcalidrawElement[],
                excalidrawAPIRef.current.getAppState()
            )
        );

        if (session?.type === SessionTypes.Annotations) {
            if (!isInControl) {
                mergedElements = mergedElements.filter(
                    (element) =>
                        element.customData?.student?.student_id === userInfo?.student_id &&
                        element.updated > Date.now() - 2500
                );
            }
        }

        // Add any new image files to Excalidraw from remote updates. If another participant adds an image
        const newFiles = getNewFilesFromElements(mergedElements, excalidrawAPIRef.current);
        if (newFiles && newFiles.length > 0) {
            excalidrawAPIRef.current?.addFiles(newFiles);
        }

        // Update versions given the remote changes, otherwise remote changes will be interpreted as local changes.
        updateVersionTracker(mergedElements);

        const sceneData = {
            ...excalidrawAPIRef.current.getAppState(),
            elements: mergedElements,
            commitToHistory: true
        };
        sceneData.bounds = initialData?.data?.appState?.bounds || null;
        mergedElements.forEach((element) => {
            if (element?.customData?.isBackground) {
                sceneData.bounds = {
                    x: element.x,
                    y: element.y,
                    width: element.width,
                    height: element.height
                };
            }
        });
        excalidrawAPIRef.current.updateScene(sceneData);
    }, [excalidrawAPIRef, mergeRemoteChanges, session?.type, updateVersionTracker, initialData?.data?.appState?.bounds, isInControl, userInfo?.student_id]);

    useEffect(() => {
        const innerFunc = async () => {
            lastIntervalTime.current = Date.now();
            if (excalidrawAPIRef.current) {
                applyRemoteUpdates();
            }
        };
        const timeout = setTimeout(
            () => {
                innerFunc();
            },
            Math.max(0, SYNC_INTERVAL - (Date.now() - lastIntervalTime.current))
        );

        const interval = setInterval(async () => {
            innerFunc();
        }, SYNC_INTERVAL);
        return () => {
            clearTimeout(timeout);
            clearInterval(interval);
        };
    }, [applyRemoteUpdates, dispatchWhiteboardSessionAction, persist, excalidrawAPIRef]);

    /**
     * Internal loop for persisting local changes and applying remote changes.
     */
    useEffect(() => {
        const interval = setInterval(async () => {
            if (excalidrawAPIRef.current) {
                persist(excalidrawAPIRef.current.getAppState());
                //const allIds = persist(excalidrawAPIRef.current.getAppState());
                //if (allIds.length > 0) {
                //    dispatchWhiteboardSessionAction({ type: ElementActions.Clear, payload: { elementIds: allIds } });
                //}
                //applyRemoteUpdates();
            }
        }, SYNC_INTERVAL * 4);
        return () => {
            clearInterval(interval);
        };
    }, [applyRemoteUpdates, dispatchWhiteboardSessionAction, persist, excalidrawAPIRef]);

    /**
     * Internal loop for tracking viewport.
     */
    useEffect(() => {
        const interval = setInterval(async () => {
            if (excalidrawAPIRef.current) {
                const appState = excalidrawAPIRef.current.getAppState();
                const newViewPort = {
                    x: -appState.scrollX,
                    y: -appState.scrollY,
                    width: appState.width,
                    height: appState.height,
                    zoom: { value: appState.zoom.value }
                };
                if (JSON.stringify(newViewPort) !== JSON.stringify(viewPort)) {
                    (window as any).viewPort = newViewPort;
                    setViewPort(newViewPort);
                }
            }
        }, SYNC_VIEWPORT_INTERVAL);
        return () => {
            clearInterval(interval);
        };
    }, [setViewPort, viewPort, excalidrawAPIRef]);
    
    const setCustomDataOnElement = useCallback((element, pageId) => {
        if (!element.customData) {
            element.customData = {
                ...element.customData
            };
        }
        element.customData.page_id = pageId;
        if (!element.customData.student) {
            element.customData.student = {
                student_id: userInfo?.student_id,
                user_name: userInfo?.userName,
                role: userInfo?.role
            };
        }
        if (!element.customData.editors) {
            element.customData.editors = {}
        }
        if (userInfo?.student_id) {
            element.customData.editors[userInfo?.student_id] = Date.now();
        }
    }, [userInfo?.role, userInfo?.student_id, userInfo?.userName]);

    const syncElement = useCallback((page, elementId, isDeleted) => {
        const element = excalidrawAPIRef.current!.getSceneElements().find((element) => element.id === elementId);
        if (isDeleted) {
            console.log('deleting Element', elementId);
            dispatchWhiteboardSessionAction({
                type: ElementActions.Deleted,
                payload: { element_ids: [elementId] }
            });
            deleteElements([elementId]);
        } else if (element && userInfo) {
            const customElement = element as any;
            setCustomDataOnElement(customElement, page?.page_id)
            console.log('syncing Element', customElement);
            dispatchWhiteboardSessionAction({ type: ElementActions.Updated, payload: { element } })
            saveElements(page, [customElement], true);
        }
    }, [excalidrawAPIRef, userInfo, dispatchWhiteboardSessionAction, deleteElements, saveElements, setCustomDataOnElement]);

    /**
     * Internal loop for persisting local changes and applying remote changes.
     */
    useEffect(() => {
        const interval = setInterval(async () => {
            if (excalidrawAPIRef.current) {
                editingElements.current.forEach((obj, id) => {
                    if (!obj.synced) {
                        if (Date.now() - obj.updated < 1000) {
                            console.log('should sync Element', id, obj);
                            editingElements.current.get(id).synced = Date.now();
                            syncElement(obj.page, id, obj.isDeleted);
                        }
                        //if (Date.now() - obj.updated > 1000) {
                        //    editingElements.current.delete(id);
                        //}
                    } else if (obj.synced < Date.now() - 500) {
                        if (Date.now() - obj.updated < 1000) {
                            console.log('should sync Element', id, obj);
                            editingElements.current.get(id).synced = Date.now();
                            syncElement(obj.page, id, obj.isDeleted);
                        }
                    }
                });
            }
        }, 300);

        return () => {
            clearInterval(interval);
        };
    }, [editingElements, excalidrawAPIRef, syncElement]);

    /**
     * This function is called whenever the Excalidraw app component updates: `componentDidUpdate()`.
     * This will be invoked very frequently!! Keep in mind this is called when:
     *
     * - Editing elements directly (drawing shapes, drawing lines, typing text)
     * - External updates from websocket
     * - I suspect any time the Excalidraw component is rendered
     * - Any other calls that impact the Excalidraw component, especially:
     *  - Collaborator info updates (cursor positions)
     *  - Excalidraw state changes from interface (such as selecting tools or changing element properties)
     *
     * Do whatever we can to save processing/networking bandwidth.
     *
     * This should really only do work if elements (and files) are added or updated locally.
     *
     * @param {ExcalidrawElement[]} elements
     * @param {AppState} appState
     * @param {Record<string, BinaryFileData>} files
     */
    const handleChange = useCallback(
        (elements: readonly ExcalidrawElement[], appState: AppState, files: BinaryFiles | EmbeddedFiles) => {
            if (!excalidrawAPIRef.current || !userInfo) return;

            setActiveTool((prev) => (appState.activeTool.type !== prev?.type ? appState.activeTool : prev));
            const elementsById = {};
            elements.forEach((element) => {
                elementsById[element.id] = element;
            });

            if (appState.editingElement) {
                const editingElementId = appState.editingElement.id;
                const editingElement = elementsById[editingElementId];
                if (editingElement) {
                    setCustomDataOnElement(editingElement, currentPage?.page_id)
                    let cachedValue = '';
                    if ((editingElement as any).text) {
                        cachedValue = (editingElement as any).text
                    }
                    if (editingElements.current.has(editingElementId)) {
                        if (editingElements.current.get(editingElementId).version !== editingElement.version || editingElements.current.get(editingElementId).cachedValue !== cachedValue) {
                            editingElements.current.set(editingElementId, {
                                updated: Date.now(),
                                version: editingElement.version,
                                versionNonce: editingElement.versionNonce,
                                cachedValue,
                                synced: false,
                                page: currentPage,
                                isDeleted: false
                            });
                        }
                    } else {
                        editingElements.current.set(editingElementId, {
                            updated: Date.now(),
                            version: editingElement.version,
                            versionNonce: editingElement.versionNonce,
                            cachedValue,
                            synced: false,
                            page: currentPage,
                            isDeleted: false
                        });
                    }
                }
            }

            if (appState.selectedElementIds && Object.keys(appState.selectedElementIds).length > 0) {
                for (const selectedElementId of Object.keys(appState.selectedElementIds)) {
                    const selectedElement = elementsById[selectedElementId];
                    if (selectedElement) {
                        setCustomDataOnElement(selectedElement, currentPage?.page_id)
                        let cachedValue = '';
                        if ((selectedElement as any).text) {
                            cachedValue = (selectedElement as any).text
                        }
                        if (editingElements.current.has(selectedElementId)) {
                            if (editingElements.current.get(selectedElementId).version !== selectedElement.version || editingElements.current.get(selectedElementId).cachedValue !== cachedValue) {
                                editingElements.current.set(selectedElementId, {
                                    updated: Date.now(),
                                    version: selectedElement.version,
                                    versionNonce: selectedElement.versionNonce,
                                    cachedValue,
                                    synced: false,
                                    page: currentPage,
                                    isDeleted: false
                                });
                            }
                        } else {
                            editingElements.current.set(selectedElementId, {
                                updated: Date.now(),
                                version: selectedElement.version,
                                versionNonce: selectedElement.versionNonce,
                                cachedValue,
                                synced: false,
                                page: currentPage,
                                isDeleted: false
                            });
                        }
                    }
                }
            }
            const missingSelectedElementIds = {};
            const newSelectedElementIds = {};
            lastSelectedElementIds.current.forEach((id) => {
                if (!appState.selectedElementIds[id]) {
                    missingSelectedElementIds[id] = true;
                }
            });
            Object.keys(appState.selectedElementIds).forEach((id) => {
                if (!lastSelectedElementIds.current.includes(id)) {
                    newSelectedElementIds[id] = true;
                }
            });
            if (Object.keys(missingSelectedElementIds).length > 0) {
                for (const selectedElementId of Object.keys(missingSelectedElementIds)) {
                    const selectedElement = elementsById[selectedElementId];
                    if (selectedElement && selectedElement.isDeleted) {
                        editingElements.current.set(selectedElementId, {
                            updated: Date.now(),
                            version: selectedElement.version,
                            versionNonce: selectedElement.versionNonce,
                            cachedValue: '',
                            synced: false,
                            page: currentPage,
                            isDeleted: true
                        });
                    }
                }
            }
            lastSelectedElementIds.current = Object.keys(appState.selectedElementIds);
            
            if (Object.keys(missingSelectedElementIds).length > 0 || Object.keys(newSelectedElementIds).length > 0) {
                setSelectedElementIds((prev) => {
                    const selectedIds = Object.keys(appState.selectedElementIds).filter(
                        (id) => ![appState.selectionElement?.id, lastSelectionElement?.id].includes(id)
                    );
                    if (prev.toString() !== selectedIds.toString()) {
                        return selectedIds;
                    }
                    return prev;
                });
                if (appState.selectionElement && appState.selectionElement !== lastSelectionElement) {
                    // Save copy of selection element before it's removed.
                    setLastSelectionElement(appState.selectionElement);
                }
            }

            // Update zoom if it changed.
            setZoom((previousZoom) => (previousZoom !== appState.zoom.value ? appState.zoom.value : previousZoom));

            // Local changes were made.
            if (getSceneVersion(elements) <= lastTrackedVersion.current) {
                return;
            }

            if (Object.keys(files).length !== fileIds.current.length) {
                dispatchWhiteboardSessionAction({ type: FileActions.Set, payload: { localFiles: files } });

                // Only add new files to external source. No file updating needed at the moment (users cannot edit image data).
                Object.keys(files)
                    .filter((fileId) => !fileIds.current.includes(fileId))
                    .filter((fileId) => !files[fileId].dataURL.startsWith('http'))
                    // Don't include files embedded from local source (e.g. preloaded math symbols).
                    .filter((fileId) => !(files[fileId] as EmbeddedFileData).embedded)
                    .map((newFileId) => files[newFileId])
                    .forEach((file) => {
                        const imageElements = elements
                            .filter((element) => element.type === 'image')
                            .map((element) => element as ExcalidrawImageElement)
                            .filter((element) => !element.isDeleted)
                            .filter((element) => element.fileId === file.id);

                        excludedImageElementIdsRef.current = [
                            ...excludedImageElementIdsRef.current,
                            ...imageElements.map((e) => e.id)
                        ];

                        // Save new image elements outside the normal persistence loop.
                        delegateImage(currentPage!, imageElements, file);
                        setTimeout(() => {
                            delegateImage(currentPage!, imageElements, file);
                        }, 1000);
                        setTimeout(() => {
                            delegateImage(currentPage!, imageElements, file);
                        }, 5000);
                    });
            }

            fileIds.current = Object.keys(files);

        },
        [excalidrawAPIRef, userInfo, setActiveTool, lastSelectionElement, currentPage, dispatchWhiteboardSessionAction, delegateImage, setCustomDataOnElement]
    );

    /**
     * Called when user selects a tool.
     */
    const handleToolTypeChange = useCallback(
        (newToolType: string) => {
            if (!excalidrawAPIRef.current || !session) {
                return;
            }

            excalidrawAPIRef.current.setActiveTool({ type: newToolType });
        },
        [excalidrawAPIRef, session]
    );

    /**
     * Apply zoom from custom UI to Excalidraw.
     */
    const handleZoomChange = useCallback(
        (newZoom: number) => {
            if (!excalidrawAPIRef.current) {
                return;
            }

            const appState = excalidrawAPIRef.current!.getAppState();
            const newZoom2 = getStateForZoom(
                {
                    viewportX: Math.round(appState.width / 2 + appState.offsetLeft),
                    viewportY: Math.round(appState.height / 2 + appState.offsetTop),
                    nextZoom: getNormalizedZoom(newZoom)
                },
                appState
            );
            excalidrawAPIRef.current!.updateScene({
                appState: {
                    ...appState,
                    ...newZoom2
                }
            });
        },
        [excalidrawAPIRef]
    );

    const move = useCallback(
        (moveFunction: MoveOperation) => {
            if (!excalidrawAPIRef.current) {
                return;
            }

            try {
                const elements = moveFunction(
                    excalidrawAPIRef.current.getSceneElements().filter((e) => selectedElementIds.includes(e.id)),
                    excalidrawAPIRef.current.getSceneElements() as NonDeletedExcalidrawElement[]
                );
                excalidrawAPIRef.current.updateScene({ elements });
            } catch (e) {
                // Failed move operation should not break whiteboard.
                // TODO: Add toast message when enhanced error handling is implemented.
                console.error(e);
            }
        },
        [selectedElementIds, excalidrawAPIRef]
    );

    const handleMoveToBack = useCallback(() => move(moveToBack), [move]);
    const handleMoveToFront = useCallback(() => move(moveToFront), [move]);
    const handleMoveBackward = useCallback(() => move(moveBackward), [move]);
    const handleMoveForward = useCallback(() => move(moveForward), [move]);

    useMemo(() => {
        // Tools which do not require an elementOptionsPanel.
        const excludedTools = ['hand', 'selection'];
        if (
            excalidrawAPIRef.current &&
            activeTool &&
            (excludedTools.indexOf(activeTool.type) === -1 || selectedElementIds.length > 0)
        ) {
            setElementOptionsPanel(
                <ElementOptionsPanel
                    show={selectedElementIds.length > 0 || excludedTools.indexOf(activeTool.type) === -1}
                    selectedElementIds={selectedElementIds}
                    activeTool={activeTool}
                    gridModeEnabled={gridModeEnabled}
                    onGridModeChange={enableGridMode}
                    excalidrawAPI={excalidrawAPIRef.current}
                    libraryGroups={libraryGroups}
                    onMoveToBack={handleMoveToBack}
                    onMoveToFront={handleMoveToFront}
                    onMoveBackward={handleMoveBackward}
                    onMoveForward={handleMoveForward}
                    onToolTypeChange={handleToolTypeChange}
                />
            );
            return;
        }
        setElementOptionsPanel(null);
    }, [
        excalidrawAPIRef,
        activeTool,
        setElementOptionsPanel,
        selectedElementIds,
        gridModeEnabled,
        libraryGroups,
        handleMoveToBack,
        handleMoveToFront,
        handleMoveBackward,
        handleMoveForward,
        handleToolTypeChange
    ]);

    useMemo(() => {
        if (session?.type !== SessionTypes.Annotations) {
            setZoomTools(<ZoomTools excalidrawZoom={zoom} onZoomChange={handleZoomChange} />);
        }
    }, [zoom, handleZoomChange, setZoomTools, session]);

    const hotkeyCallbacks = useMemo(
        () => ({
            [MOVE_BACKWARD_HOTKEYS]: moveBackward,
            [MOVE_FORWARD_HOTKEYS]: moveForward,
            [MOVE_TO_BACK_HOTKEYS]: moveToBack,
            [MOVE_TO_FRONT_HOTKEYS]: moveToFront
        }),
        []
    );

    useHotkeys(
        ALL_HOTKEYS,
        (keyboardEvent, hotkeysEvent) => {
            keyboardEvent.preventDefault();
            keyboardEvent.stopImmediatePropagation();
            const callback = Object.keys(hotkeyCallbacks).find((key) => key.split(', ').includes(hotkeysEvent.key));
            if (callback) move(hotkeyCallbacks[callback]);
        },
        [move]
    );

    const loadingElement =
        whiteboardSessionState?.session?.type === SessionTypes.Presentation ? (
            <Loading sx={{ width: '100%' }} backgroundColor={'rgb(50, 50, 50)'} />
        ) : (
            <Loading />
        );

    let borderColor = '#667080';
    let borderWidth = 0;

    if (
        presenter &&
        isInControl &&
        (whiteboardSessionState?.session?.type === SessionTypes.Annotations ||
            whiteboardSessionState?.session?.type === SessionTypes.Shared ||
            whiteboardSessionState?.session?.type === SessionTypes.Presentation ||
            presenter.shareStudentPages)
    ) {
        borderColor = '#ffba00';
        borderWidth = 3;
    } else if (
        presenter &&
        isInControl &&
        whiteboardSessionState?.session?.type === SessionTypes.Individual &&
        !presenter.shareStudentPages
    ) {
        borderColor = '#ffba00';
        borderWidth = 3;
    }

    const border = `${borderWidth}px solid ${borderColor}`;
    const note = showNotes && currentPage?.data?.note;

    return (
        <Layout sx={[hideToolbar && { height: '100%' }]}>
            <InnerLayout id="excallidraw_controller_box">
                <Stack flexGrow={1}>
                    {session?.type !== SessionTypes.Annotations && <MoveCanvasHelpChip />}
                    {excalidrawAPIRef.current && !readonly && (
                        <>
                            <ExportModal
                                open={exportModalOpen}
                                excalidrawAPI={excalidrawAPIRef.current}
                                onClose={() => setExportModalOpen(false)}
                            />
                        </>
                    )}

                    <WhiteboardContent>
                        {loading && loadingElement}
                        {!loading && (
                            <ExcalidrawWrapper
                                boxProps={{
                                    sx: {
                                        border
                                    }
                                }}
                                initialExcalidrawData={{
                                    key: initialData!.key,
                                    data: {
                                        ...initialData?.data,
                                        appState: {
                                            viewBackgroundColor: '#ffffff',
                                            ...initialData?.data?.appState,
                                            zoom: viewPort.zoom,
                                            transparent: session?.type === SessionTypes.Annotations
                                        }
                                    }
                                }}
                                excalidrawRefCallback={excalidrawRefCallback}
                                onChange={handleChange}
                                gridModeEnabled={gridModeEnabled}
                                viewModeEnabled={readonly}
                            />
                        )}
                    </WhiteboardContent>
                </Stack>
            </InnerLayout>
            {presenter && isInControl && note && (
                <Note>
                    <CloseNotesIcon onClick={() => setShowNotes(false)}></CloseNotesIcon>
                    <Typography variant="caption">{t('notesHeader')}</Typography>
                    <Typography variant="body1">{note}</Typography>
                </Note>
            )}
        </Layout>
    );
};
